/**
 * useCreditSystem Hook
 *
 * Provides credit system state and data, handling both
 * legacy EUR billing and new Credit system based on feature flag.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  getCreditBalance,
  getCreditPackages,
  getCreditPricing,
} from '@/lib/api/credits';
import type {
  CreditBalance,
  CreditPackage,
  CreditPricing,
} from '@/types/credits';

interface CreditSystemConfig {
  enabled: boolean;
  activation_date: string | null;
  is_active_now: boolean;
}

interface CreditSystemState {
  /** Whether the credit system is active (vs legacy EUR billing) */
  isActive: boolean;
  /** Whether data is still loading */
  isLoading: boolean;
  /** Credit balance (only when credit system is active) */
  balance: CreditBalance | null;
  /** Available credit packages */
  packages: CreditPackage[];
  /** Credit pricing for features */
  pricing: CreditPricing[];
  /** Current package the workspace is on */
  currentPackage: CreditPackage | null;
  /** Refetch all credit data */
  refetch: () => void;
}

export function useCreditSystem(): CreditSystemState {
  // Check if credit system is active
  const { data: configData, isLoading: configLoading } = useQuery({
    queryKey: ['credit-system-config'],
    queryFn: async () => {
      try {
        const res = await apiClient.get<CreditSystemConfig>(
          '/api/v2/credits/config/status'
        );
        return res.data;
      } catch {
        // If endpoint doesn't exist or fails, assume credit system is not active
        return { enabled: false, activation_date: null, is_active_now: false };
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const isActive = configData?.is_active_now ?? false;

  // Load credit balance (only when credit system is active)
  const {
    data: balanceData,
    isLoading: balanceLoading,
    refetch: refetchBalance,
  } = useQuery({
    queryKey: ['credit-balance'],
    queryFn: getCreditBalance,
    enabled: isActive,
    staleTime: 1000 * 30, // 30 seconds
  });

  // Load credit packages
  const {
    data: packagesData,
    isLoading: packagesLoading,
    refetch: refetchPackages,
  } = useQuery({
    queryKey: ['credit-packages'],
    queryFn: getCreditPackages,
    enabled: isActive,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Load credit pricing
  const {
    data: pricingData,
    isLoading: pricingLoading,
    refetch: refetchPricing,
  } = useQuery({
    queryKey: ['credit-pricing'],
    queryFn: getCreditPricing,
    enabled: isActive,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const isLoading =
    configLoading ||
    (isActive && (balanceLoading || packagesLoading || pricingLoading));

  // Find current package based on balance
  const currentPackage =
    packagesData?.packages?.find(
      (p) => p.id === balanceData?.balance?.package_id
    ) ?? null;

  const refetch = () => {
    refetchBalance();
    refetchPackages();
    refetchPricing();
  };

  return {
    isActive,
    isLoading,
    balance: balanceData?.balance ?? null,
    packages: packagesData?.packages ?? [],
    pricing: pricingData?.pricing ?? [],
    currentPackage,
    refetch,
  };
}
